import { useState, useRef, useMemo } from 'react';
import { Wallet as WalletIcon, QrCode, Send, Scan, Download, Filter, X, Printer, Eye, Calendar, DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useWalletSystem } from '@/hooks/useWalletSystem';
import { useUser } from '@/contexts/UserContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Html5Qrcode } from 'html5-qrcode';
import { useCustomers } from '@/hooks/useCustomers';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export default function Wallet() {
  const { user } = useUser();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { wallet, transactions, transfer, isTransferring } = useWalletSystem();
  const { customers } = useCustomers();

  const qrCodeRef = useRef<HTMLDivElement>(null);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  // Transfer form state (USD only)
  const [transferData, setTransferData] = useState({
    toCustomerCode: '',
    currency: 'USD',
    amount: '',
    description: '',
  });

  // Find recipient customer
  const recipientCustomer = customers.find(c => c.code === transferData.toCustomerCode);

  // Start QR scanner
  const startScanner = async () => {
    setScannerActive(true);
    const html5QrCode = new Html5Qrcode("qr-reader");
    
    try {
      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        (decodedText) => {
          setTransferData(prev => ({ ...prev, toCustomerCode: decodedText }));
          html5QrCode.stop();
          setScannerActive(false);
          setShowScanner(false);
          setShowTransfer(true);
        },
        () => {}
      );
    } catch (err) {
      console.error("Error starting scanner:", err);
      setScannerActive(false);
    }
  };

  // Handle transfer submission
  const handleTransfer = () => {
    if (!wallet || !user?.customerCode) return;
    
    transfer({
      fromCustomerCode: user.customerCode,
      toCustomerCode: transferData.toCustomerCode,
      currency: transferData.currency,
      amount: parseFloat(transferData.amount),
      description: transferData.description || 'Transfer',
    }, {
      onSuccess: () => {
        setShowTransfer(false);
        setTransferData({
          toCustomerCode: '',
          currency: 'USD',
          amount: '',
          description: '',
        });
      }
    });
  };



  // Download QR code as image
  const downloadQRCode = () => {
    const svg = qrCodeRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    canvas.width = 400;
    canvas.height = 480;

    img.onload = () => {
      if (!ctx) return;
      
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 100, 80, 200, 200);
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 20px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(user?.customerName || '', 200, 320);
      ctx.font = '16px Inter, sans-serif';
      ctx.fillStyle = '#666666';
      ctx.fillText(`Code: ${user?.customerCode || ''}`, 200, 350);
      ctx.font = '14px Inter, sans-serif';
      ctx.fillStyle = '#999999';
      ctx.fillText('Scan to Send Money', 200, 380);

      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `wallet-qr-${user?.customerCode}.png`;
        a.click();
        URL.revokeObjectURL(url);
      });
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    if (!transactions) return [];

    return transactions.filter((txn: any) => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || 
        (txn.transaction_number || '').toLowerCase().includes(searchLower) ||
        (txn.description || '').toLowerCase().includes(searchLower) ||
        (txn.from_customer_name || '').toLowerCase().includes(searchLower) ||
        (txn.to_customer_name || '').toLowerCase().includes(searchLower);

      const matchesType = typeFilter === 'all' || txn.type === typeFilter;

      let matchesDate = true;
      if (dateRange.startDate && dateRange.endDate) {
        const txnDate = new Date(txn.created_at);
        const start = new Date(dateRange.startDate);
        const end = new Date(dateRange.endDate);
        end.setHours(23, 59, 59, 999);
        matchesDate = txnDate >= start && txnDate <= end;
      }

      return matchesSearch && matchesType && matchesDate;
    });
  }, [transactions, searchTerm, typeFilter, dateRange]);

  // Calculate statistics
  const statistics = useMemo(() => {
    if (!filteredTransactions.length) return { totalIn: 0, totalOut: 0, count: 0 };

    let totalIn = 0;
    let totalOut = 0;

    filteredTransactions.forEach((t: any) => {
      const amount = parseFloat(t.amount);
      
      // For exchange transactions, we need to handle both sides
      if (t.type === 'exchange') {
        // Money going OUT (deducted from source currency)
        totalOut += Math.abs(amount);
        
        // Money coming IN (received in target currency)
        if (t.exchanged_amount) {
          totalIn += Math.abs(parseFloat(t.exchanged_amount));
        }
      } else {
        // Regular transactions (deposits, payments, transfers)
        if (amount > 0) {
          totalIn += amount;
        } else {
          totalOut += Math.abs(amount);
        }
      }
    });

    return {
      totalIn,
      totalOut,
      count: filteredTransactions.length
    };
  }, [filteredTransactions]);

  // Active filter count
  const activeFilterCount = [
    searchTerm ? 1 : 0,
    typeFilter !== 'all' ? 1 : 0,
    dateRange.startDate && dateRange.endDate ? 1 : 0
  ].reduce((a, b) => a + b, 0);

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setTypeFilter('all');
    setDateRange({ startDate: '', endDate: '' });
  };

  // Print transaction report
  const handlePrintReport = () => {
    const logoUrl = window.location.origin + '/logo-icon.jpg';
    const printContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8"/>
    <title>Wallet Transaction Report</title>
    <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body {
            font-family: 'Arial', sans-serif;
            padding: 20mm;
            background: #fff;
        }
        .header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 3px solid #1e3a6e;
        }
        .header img {
            height: 50px;
        }
        .header-info {
            text-align: right;
        }
        .header-info h1 {
            color: #1e3a6e;
            font-size: 24px;
            margin-bottom: 5px;
        }
        .header-info p {
            color: #666;
            font-size: 12px;
        }
        .customer-info {
            background: #f8f9fa;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 20px;
        }
        .customer-info h3 {
            color: #1e3a6e;
            margin-bottom: 10px;
        }
        .balance-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            margin-bottom: 20px;
        }
        .balance-card {
            border: 2px solid #e0e0e0;
            border-radius: 8px;
            padding: 15px;
        }
        .balance-card h4 {
            color: #666;
            font-size: 12px;
            margin-bottom: 5px;
        }
        .balance-card .amount {
            font-size: 28px;
            font-weight: bold;
            color: #1e3a6e;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
        }
        th {
            background: #1e3a6e;
            color: white;
            padding: 12px 8px;
            text-align: left;
            font-size: 11px;
            font-weight: 600;
        }
        td {
            padding: 10px 8px;
            border-bottom: 1px solid #e0e0e0;
            font-size: 10px;
        }
        tr:hover {
            background: #f8f9fa;
        }
        .amount-positive {
            color: #28a745;
            font-weight: bold;
        }
        .amount-negative {
            color: #dc3545;
            font-weight: bold;
        }
        .footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 2px solid #e0e0e0;
            text-align: center;
            font-size: 10px;
            color: #888;
        }
        .summary {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 15px;
            margin: 20px 0;
            padding: 15px;
            background: #f8f9fa;
            border-radius: 8px;
        }
        .summary-item {
            text-align: center;
        }
        .summary-item .label {
            font-size: 11px;
            color: #666;
            margin-bottom: 5px;
        }
        .summary-item .value {
            font-size: 20px;
            font-weight: bold;
            color: #1e3a6e;
        }
        @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
    </style>
</head>
<body>
    <div class="header">
        <img src="${logoUrl}" alt="SourceMyGoods" onerror="this.style.display='none'"/>
        <div class="header-info">
            <h1>Wallet Transaction Report</h1>
            <p>Generated on ${format(new Date(), 'MMM dd, yyyy HH:mm')}</p>
        </div>
    </div>

    <div class="customer-info">
        <h3>Account Information</h3>
        <p><strong>Name:</strong> ${user?.customerName || 'N/A'}</p>
        <p><strong>Customer Code:</strong> ${user?.customerCode || 'N/A'}</p>
        <p><strong>Report Period:</strong> ${dateRange.startDate && dateRange.endDate ? `${dateRange.startDate} to ${dateRange.endDate}` : 'All Time'}</p>
    </div>

    <div class="balance-section">
        <div class="balance-card" style="grid-column: span 2;">
            <h4>Wallet Balance (USD)</h4>
            <div class="amount">$${parseFloat(wallet?.balance_usd || 0).toFixed(2)}</div>
        </div>
    </div>

    <div class="summary">
        <div class="summary-item">
            <div class="label">Total Received</div>
            <div class="value" style="color: #28a745;">$${statistics.totalIn.toFixed(2)}</div>
        </div>
        <div class="summary-item">
            <div class="label">Total Sent</div>
            <div class="value" style="color: #dc3545;">$${statistics.totalOut.toFixed(2)}</div>
        </div>
        <div class="summary-item">
            <div class="label">Transactions</div>
            <div class="value">${statistics.count}</div>
        </div>
    </div>

    <table>
        <thead>
            <tr>
                <th>Date</th>
                <th>Transaction #</th>
                <th>Type</th>
                <th>Description</th>
                <th style="text-align: right;">Amount (USD)</th>
                <th style="text-align: right;">Balance (USD)</th>
            </tr>
        </thead>
        <tbody>
            ${filteredTransactions.map((txn: any) => {
                const amount = parseFloat(txn.amount);
                return `
                <tr>
                    <td>${format(new Date(txn.created_at), 'MMM dd, yyyy HH:mm')}</td>
                    <td>${txn.transaction_number}</td>
                    <td>${txn.type}</td>
                    <td>${txn.description || 'N/A'}</td>
                    <td style="text-align: right;" class="${amount > 0 ? 'amount-positive' : 'amount-negative'}">
                        ${amount > 0 ? '+' : ''}$${Math.abs(amount).toFixed(2)}
                    </td>
                    <td style="text-align: right;">
                        $${parseFloat(txn.balance_after).toFixed(2)}
                    </td>
                </tr>
                `;
            }).join('')}
        </tbody>
    </table>

    <div class="footer">
        <strong>SourceMyGoods</strong> | Professional Sourcing, Inspection & Global Shipping<br/>
        Operated by Trust Exchange International Trading LTD
    </div>

    <script>window.onload = function() { window.print(); }</script>
</body>
</html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
    }
  };

  // Get transaction type display
  const getTransactionTypeDisplay = (type: string) => {
    const types: Record<string, { label: string, color: string }> = {
      'deposit': { label: 'Deposit', color: 'bg-green-100 text-green-800' },
      'withdrawal': { label: 'Withdrawal', color: 'bg-red-100 text-red-800' },
      'transfer_out': { label: 'Transfer Out', color: 'bg-orange-100 text-orange-800' },
      'transfer_in': { label: 'Transfer In', color: 'bg-blue-100 text-blue-800' },
      'exchange': { label: 'Exchange', color: 'bg-purple-100 text-purple-800' },
      'payment_invoice': { label: 'Invoice Payment', color: 'bg-indigo-100 text-indigo-800' },
      'payment_sourcing': { label: 'Sourcing Payment', color: 'bg-pink-100 text-pink-800' },
      'refund': { label: 'Refund', color: 'bg-green-100 text-green-800' },
    };
    return types[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
  };

  if (!user || user.role === 'admin') {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">{t('wallet')}</h1>
        </div>
        <Card>
          <CardContent className="py-12">
            <p className="text-center text-muted-foreground">
              Wallet feature is only available for customers.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!wallet) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">{t('wallet')}</h1>
        </div>
        <Card>
          <CardContent className="py-12">
            <p className="text-center text-muted-foreground">Loading wallet...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">{t('wallet')}</h1>
          <p className="text-muted-foreground mt-1">Manage your wallet balance and transactions</p>
        </div>
      </div>

      {/* Balance Card with QR Code */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* USD Balance */}
        <Card className="border-2 border-blue-200">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Wallet Balance (USD)
            </CardDescription>
            <CardTitle className="text-4xl">
              ${parseFloat(wallet.balance_usd || 0).toFixed(2)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button 
              size="sm"
              onClick={() => setShowTransfer(true)}
              className="w-full"
            >
              <Send className="h-3 w-3 mr-1" />
              Transfer Money
            </Button>
          </CardContent>
        </Card>

        {/* QR Code Card */}
        <Card className="border-2 border-gray-200">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <QrCode className="h-4 w-4" />
              My QR Code
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center gap-3">
            <div ref={qrCodeRef} className="p-2 bg-white rounded-lg border">
              <QRCodeSVG 
                value={user.customerCode || ''} 
                size={100}
                level="H"
              />
            </div>
            <Button onClick={downloadQRCode} variant="outline" size="sm" className="w-full">
              <Download className="h-3 w-3 mr-1" />
              Download
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Manage your wallet with these quick actions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2"
              onClick={() => setShowScanner(true)}
            >
              <Scan className="h-6 w-6" />
              <span className="text-sm">Scan QR</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2"
              onClick={() => setShowTransfer(true)}
            >
              <Send className="h-6 w-6" />
              <span className="text-sm">Transfer</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Transaction Statistics */}
      {filteredTransactions.length > 0 && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-green-600" />
                Total Received
              </CardDescription>
              <CardTitle className="text-2xl text-green-600">
                ${statistics.totalIn.toFixed(2)}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center gap-2">
                <TrendingDown className="h-4 w-4 text-red-600" />
                Total Sent
              </CardDescription>
              <CardTitle className="text-2xl text-red-600">
                ${statistics.totalOut.toFixed(2)}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Transactions
              </CardDescription>
              <CardTitle className="text-2xl">
                {statistics.count}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Transaction History</CardTitle>
              <CardDescription>Complete record of all wallet transactions</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-2">{activeFilterCount}</Badge>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrintReport}
                disabled={!filteredTransactions.length}
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Report
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          {showFilters && (
            <div className="mb-6 p-4 border rounded-lg bg-muted/50 space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Search</Label>
                  <Input
                    placeholder="Transaction #, description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="deposit">Deposit</SelectItem>
                      <SelectItem value="withdrawal">Withdrawal</SelectItem>
                      <SelectItem value="transfer_in">Transfer In</SelectItem>
                      <SelectItem value="transfer_out">Transfer Out</SelectItem>
                      <SelectItem value="payment_invoice">Invoice Payment</SelectItem>
                      <SelectItem value="payment_sourcing">Sourcing Payment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date Range</Label>
                  <div className="flex gap-2">
                    <Input
                      type="date"
                      value={dateRange.startDate}
                      onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                    />
                    <Input
                      type="date"
                      value={dateRange.endDate}
                      onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              {activeFilterCount > 0 && (
                <div className="flex justify-end">
                  <Button variant="ghost" size="sm" onClick={resetFilters}>
                    <X className="h-4 w-4 mr-2" />
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Transaction Table */}
          {filteredTransactions && filteredTransactions.length > 0 ? (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Transaction #</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Balance After</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.map((txn: any) => {
                    const typeDisplay = getTransactionTypeDisplay(txn.type);
                    return (
                      <TableRow key={txn.id}>
                        <TableCell className="font-mono text-xs">
                          {format(new Date(txn.created_at), 'MMM dd, yyyy')}
                          <br />
                          <span className="text-muted-foreground">
                            {format(new Date(txn.created_at), 'HH:mm:ss')}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {txn.transaction_number}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={typeDisplay.color}>
                            {typeDisplay.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {txn.description || 'N/A'}
                        </TableCell>
                        <TableCell className={`text-right font-semibold ${
                          parseFloat(txn.amount) > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {parseFloat(txn.amount) > 0 ? '+' : ''}
                          ${Math.abs(parseFloat(txn.amount || 0)).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ${parseFloat(txn.balance_after || 0).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedTransaction(txn);
                              setShowDetailDialog(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <div className="mx-auto w-24 h-24 mb-4 rounded-full bg-muted flex items-center justify-center">
                <WalletIcon className="h-12 w-12 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">
                {activeFilterCount > 0 ? 'No transactions match your filters' : 'No transactions yet'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transaction Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
            <DialogDescription>
              Complete information about this transaction
            </DialogDescription>
          </DialogHeader>
          {selectedTransaction && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Transaction Number</Label>
                  <p className="font-mono font-medium">{selectedTransaction.transaction_number}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Type</Label>
                  <p className="font-medium">{getTransactionTypeDisplay(selectedTransaction.type).label}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Date & Time</Label>
                  <p className="font-medium">{format(new Date(selectedTransaction.created_at), 'MMM dd, yyyy HH:mm:ss')}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Currency</Label>
                  <p className="font-medium">USD</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Amount</Label>
                  <p className={`font-semibold text-lg ${parseFloat(selectedTransaction.amount) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {parseFloat(selectedTransaction.amount) > 0 ? '+' : ''}
                    ${Math.abs(parseFloat(selectedTransaction.amount)).toFixed(2)}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Balance After</Label>
                  <p className="font-semibold text-lg">
                    ${parseFloat(selectedTransaction.balance_after).toFixed(2)}
                  </p>
                </div>
              </div>
              
              <Separator />
              
              {selectedTransaction.from_customer_code && (
                <div>
                  <Label className="text-xs text-muted-foreground">From</Label>
                  <p className="font-medium">{selectedTransaction.from_customer_name} ({selectedTransaction.from_customer_code})</p>
                </div>
              )}
              
              {selectedTransaction.to_customer_code && (
                <div>
                  <Label className="text-xs text-muted-foreground">To</Label>
                  <p className="font-medium">{selectedTransaction.to_customer_name} ({selectedTransaction.to_customer_code})</p>
                </div>
              )}
              
              {selectedTransaction.exchange_rate && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Exchange Rate</Label>
                    <p className="font-medium">{parseFloat(selectedTransaction.exchange_rate).toFixed(6)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">From Currency</Label>
                    <p className="font-medium">{selectedTransaction.from_currency}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">To Currency</Label>
                    <p className="font-medium">{selectedTransaction.to_currency}</p>
                  </div>
                </div>
              )}
              
              {selectedTransaction.reference_number && (
                <div>
                  <Label className="text-xs text-muted-foreground">Reference</Label>
                  <p className="font-medium">{selectedTransaction.reference_type}: {selectedTransaction.reference_number}</p>
                </div>
              )}
              
              <div>
                <Label className="text-xs text-muted-foreground">Description</Label>
                <p className="font-medium">{selectedTransaction.description || 'N/A'}</p>
              </div>
              
              {selectedTransaction.created_by && (
                <div>
                  <Label className="text-xs text-muted-foreground">Created By</Label>
                  <p className="font-medium">{selectedTransaction.created_by}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QR Scanner Dialog */}
      <Dialog open={showScanner} onOpenChange={(open) => {
        setShowScanner(open);
        if (!open && scannerActive) {
          const html5QrCode = new Html5Qrcode("qr-reader");
          html5QrCode.stop().catch(() => {});
          setScannerActive(false);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Scan QR Code</DialogTitle>
            <DialogDescription>
              Scan recipient's wallet QR code
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div id="qr-reader" className="w-full"></div>
            {!scannerActive && (
              <Button onClick={startScanner} className="w-full mt-4">
                Start Scanner
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={showTransfer} onOpenChange={setShowTransfer}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer Money</DialogTitle>
            <DialogDescription>
              Transfer money to another customer
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Recipient Customer Code</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter customer code"
                  value={transferData.toCustomerCode}
                  onChange={(e) => setTransferData(prev => ({ ...prev, toCustomerCode: e.target.value }))}
                />
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowTransfer(false);
                    setShowScanner(true);
                  }}
                >
                  <Scan className="h-4 w-4" />
                </Button>
              </div>
              {recipientCustomer && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="font-medium text-green-900">{recipientCustomer.name}</p>
                  <p className="text-sm text-green-700">Code: {recipientCustomer.code}</p>
                </div>
              )}
              {transferData.toCustomerCode && !recipientCustomer && (
                <p className="text-sm text-red-600">Customer not found</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Amount (USD)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={transferData.amount}
                onChange={(e) => setTransferData(prev => ({ ...prev, amount: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Available: ${parseFloat(wallet.balance_usd || 0).toFixed(2)}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input
                placeholder="What's this for?"
                value={transferData.description}
                onChange={(e) => setTransferData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <Button 
              onClick={handleTransfer} 
              disabled={!recipientCustomer || !transferData.amount || isTransferring}
              className="w-full"
            >
              {isTransferring ? 'Processing...' : 'Transfer Money'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
